import * as React from "react"

import type { Header } from "@tanstack/react-table"

import { cn } from "../../cn"
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverItem,
  PopoverTitle,
} from "../Popover/Popover"
import { useColumnResize } from "./useColumnResize"

/**
 * A column header's resize separator. Dragging it or pressing the arrow keys
 * resizes the column; a tap or Enter opens step controls that do the same
 * without a drag, for pointer users who cannot drag (WCAG 2.5.7). The popover
 * hangs off an anchor rather than a trigger, because the separator role does
 * not allow the trigger's `aria-haspopup` / `aria-expanded`.
 */
export function ColumnResizeHandle<T>({
  header,
  resizeLabel,
}: {
  header: Header<T, unknown>
  /** Column name the separator and its controls announce. */
  resizeLabel: string
}) {
  const [open, setOpen] = React.useState(false)
  const { isDragging, isSized, resizeBy, resetWidth, separatorProps } =
    useColumnResize(header, () => setOpen(true))
  const titleId = React.useId()
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        {/* Keep the pointer target wider than the visible divider so resizing remains easy to grab. */}
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label={`Resize ${resizeLabel} column`}
          tabIndex={0}
          {...separatorProps}
          className={cn(
            "absolute right-0 top-1 bottom-1 w-[5px] cursor-col-resize touch-none select-none",
            "after:absolute after:inset-y-0 after:right-0 after:w-px after:bg-coval-border-strong after:content-['']",
            "opacity-0 transition-opacity hover:opacity-100",
            "focus-visible:opacity-100",
            (isDragging || open) && "opacity-100",
          )}
        />
      </PopoverAnchor>
      <PopoverContent
        aria-labelledby={titleId}
        align="center"
        className="w-44 p-1"
        // Back to the separator, which has no trigger for Radix to return to.
        onCloseAutoFocus={(event) => {
          event.preventDefault()
          separatorProps.ref.current?.focus()
        }}
      >
        <PopoverTitle id={titleId}>{resizeLabel} column width</PopoverTitle>
        <PopoverItem onClick={() => resizeBy(-1)}>Narrower</PopoverItem>
        <PopoverItem onClick={() => resizeBy(1)}>Wider</PopoverItem>
        <PopoverItem disabled={!isSized} onClick={resetWidth}>
          Reset width
        </PopoverItem>
      </PopoverContent>
    </Popover>
  )
}
